package one.healthium.insights.ui.content

import co.touchlab.kermit.Logger
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.withIndex
import one.healthium.base.preferences.AppPreferences
import one.healthium.channels.MealPlanChannel
import one.healthium.insights.domain.content.ContentOptions
import one.healthium.insights.domain.content.ContentRepository
import one.healthium.insights.domain.models.ActionModel
import one.healthium.insights.domain.models.ContentScreenModel
import one.healthium.insights.domain.models.SavedContentModel
import one.healthium.insights.domain.models.SavedContentOptions
import one.healthium.ui.common.message.UiMessage
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel
import kotlin.time.Clock

internal class ContentViewModel(
    private val contentRepository: ContentRepository,
    private val contentEvents: ContentEvents,
    private val appPreferences: AppPreferences,
    private val melPlanChannel: MealPlanChannel,
) : BaseViewModel() {

    private val _state = MutableStateFlow(ContentUiState())

    val messages = UiMessageManager()

    init {
        appPreferences.isMetricFlow
            .onEach { isMetrics ->
                _state.update { state -> state.copy(isMetrics = isMetrics) }
            }
            .launch()
    }

    fun state(options: ContentOptions): StateFlow<ContentUiState> {
        if (_state.value.contentOptions != options) {
            _state.update { state ->
                state.copy(
                    contentOptions = options,
                    content = null,
                )
            }
        }
        return _state.asStateFlow()
    }

    init {
        _state
            .map { it.contentOptions }
            .filterNotNull()
            .distinctUntilChanged()
            .flatMapLatest { contentOptions ->
                combine(
                    contentFlow(contentOptions),
                    favoriteFlow(contentOptions)
                ) { content, favorite -> Pair(content, favorite) }
                    .catch { e ->
                        Logger.e(e) { "Error loading content" }
                        _state.update { state -> state.copy(loading = false) }
                        if (e !is CancellationException) {
                            messages.add(UiMessage.throwable(e))
                        }
                    }
            }
            .onEach {
                _state.update { state ->
                    state.copy(
                        loading = false,
                        content = it.first,
                        savedContentModel = it.second,
                    )
                }
            }
            .launch()
    }

    fun onContentOpened(options: ContentOptions) {
        when (options) {
            is ContentOptions.Insight -> launch {
                appPreferences.latestReadInsightTimestamp = Clock.System.now().toEpochMilliseconds()
                contentEvents.onInsightOpened(options.id)
            }

            is ContentOptions.Workout -> launch { contentEvents.onWorkoutOpened(options.id) }
            is ContentOptions.MealPlan -> launch {
                appPreferences.latestReadMealPlanTimestamp = Clock.System.now().toEpochMilliseconds()
            }

            else -> Unit
        }
    }

    fun onFavoriteClicked(action: ActionModel.Favorite) {
        launch {
            try {
                val favoriteModel = _state.value.savedContentModel
                if (favoriteModel != null) {
                    contentRepository.removeContentFromFavorite(favoriteModel.id)
                } else {
                    val contentOptions = _state.value.contentOptions ?: return@launch
                    val options = SavedContentOptions(
                        contentOptions = contentOptions,
                        contentPreview = action.contentPreview,
                    )
                    contentRepository.addContentToFavorite(options)
                }
            } catch (e: Exception) {
                Logger.e(e) { "Error setting content favorite" }
                messages.add(UiMessage.throwable(e))
            }
        }
    }

    private fun contentFlow(contentOptions: ContentOptions): Flow<ContentScreenModel> {
        val contentFlow = if (contentOptions is ContentOptions.MealPlan) {
            melPlanChannel.mealPlanRefreshFlow
                .withIndex()
                .flatMapLatest {
                    _state.update { state -> state.copy(loading = true) }
                    contentRepository.getContent(
                        contentOptions,
                        force = it.index > 0
                    ) // Force refresh on every update except the first one
                }
        } else {
            _state.update { state -> state.copy(loading = true) }
            contentRepository.getContent(contentOptions)
        }
        return contentFlow
    }

    private fun favoriteFlow(contentOptions: ContentOptions): Flow<SavedContentModel?> {
        return contentRepository.getFavoriteContent()
            .map { favorites ->
                favorites.firstOrNull { it.contentOptions == contentOptions }
            }
    }
}